import type { CalendarDate } from '@internationalized/date';
import type { SupportedLocale } from '@viamichelin/business';
import { page } from '$app/state';
import { isLargeDevice, isSmallDevice } from './getPlatform';

export type ContentType = 'datasheet' | 'boxmap' | 'search_extension' | 'poilist' | 'poirecommendation' | 'arrival_zoi';
enum device {
	DESKTOP = 'desktop',
	TABLET = 'tablet',
	PHONE = 'phone',
}
export interface BookingParams {
	bookUrl?: string;
	contentType: ContentType;
	priceMinAccommodationWithCurrency?: string;
	subType?: string;
	poiId?: string;
	srcPoiId?: string;
	pageName?: string;
	arrival?: string;
	departure?: string;
	distance?: string;
	address?: string;
	checkout?: CalendarDate;
	checkin?: CalendarDate;
	triggerSE?: string;
	star?: number;
}
export type StatDomain =
	| 'at'
	| 'be'
	| 'ch'
	| 'com'
	| 'co.uk'
	| 'de'
	| 'es'
	| 'fr'
	| 'ie'
	| 'it'
	| 'nl'
	| 'pl'
	| 'pt';

/**
 * TO DO :
 * we need to add a system to manage the AbTest and fill the params {keys}|{values} in the label
 */

const TEMPLATE_LABEL
	= 'viam|{contentType}|{domain}|{page}|{device}|{departure}|{arrival}|{minimumPrice}|{hotelCategory}|{doAvailabilityCheck}|{keys}|{values}|{poiId}|{srcPoiId}|{trackingAcquisition}|{distance}|{timestamp}|{triggerSE}|{star}';
const mapLocaleToDomain: Record<SupportedLocale, StatDomain> = {
	'de-AT': 'at',
	'de-CH': 'ch',
	'de-DE': 'de',
	'en-GB': 'co.uk',
	'en-IE': 'ie',
	'en-US': 'com',
	'es-ES': 'es',
	'fr-BE': 'be',
	'fr-CH': 'ch',
	'fr-FR': 'fr',
	'it-CH': 'ch',
	'it-IT': 'it',
	'nl-BE': 'be',
	'nl-NL': 'nl',
	'pt-PT': 'pt',
};

export function getDomain() {
	const locale = page.data.locale;
	return mapLocaleToDomain[locale];
}

export function setBookingUrlLabelPagename(url: string, pagename: string) {
	const urlObj = new URL(url);
	const currentLabel = urlObj.searchParams.get('label');

	if (currentLabel) {
		const templateParts = TEMPLATE_LABEL.split('|');
		const pageIndex = templateParts.findIndex(part => part === '{page}');

		if (pageIndex !== -1) {
			const labelParts = currentLabel.split('|');
			if (labelParts.length > pageIndex) {
				labelParts[pageIndex] = pagename;
				const newLabel = labelParts.join('|');
				urlObj.searchParams.set('label', newLabel);
			}
		}
	}

	return urlObj.toString();
}

function getDeviceType() {
	if (isSmallDevice()) {
		return device.PHONE;
	} else if (isLargeDevice()) {
		return device.DESKTOP;
	}
	return device.TABLET;
}

export function getLabel(bookingParams: BookingParams) {
	const { trackingAcquisition } = page.data;

	const {
		arrival,
		contentType,
		departure,
		distance,
		priceMinAccommodationWithCurrency,
		subType,
		poiId,
		srcPoiId,
		pageName,
		star,
		checkin,
		checkout,
		triggerSE,
	} = bookingParams;

	const doAvailabilityCheck = checkin && checkout ? 'On' : 'Off';

	const label = TEMPLATE_LABEL.replace('{contentType}', contentType)
		.replace('{minimumPrice}', priceMinAccommodationWithCurrency || '')
		.replace('{hotelCategory}', subType || '')
		.replace('{poiId}', poiId || '')
		.replace('{srcPoiId}', srcPoiId || '')
		.replace('{device}', getDeviceType())
		.replace('{doAvailabilityCheck}', doAvailabilityCheck)
		.replace('{page}', pageName || '')
		.replace('{domain}', getDomain())
		.replace('{arrival}', arrival || '')
		.replace('{departure}', departure || '')
		.replace('{distance}', distance || '')
		.replace('{trackingAcquisition}', trackingAcquisition || '')
		.replace('{keys}', '')
		.replace('{values}', '')
		.replace('{timestamp}', Date.now().toString())
		.replace('{triggerSE}', triggerSE || '')
		.replace('{star}', star?.toString() || '');

	return label;
}
